import { List, useTable, EditButton, DateField } from "@refinedev/antd";
import { useList, useUpdate } from "@refinedev/core";
import {
  Table,
  Switch,
  Space,
  Typography,
  Tag,
  Card,
  Row,
  Col,
  Progress,
  Radio,
  Tooltip,
  Grid,
} from "antd";
import {
  FlagOutlined,
  CheckCircleTwoTone,
  StopTwoTone,
  ThunderboltOutlined,
} from "@ant-design/icons";
import { useMemo, useState } from "react";
import type { FeatureFlag } from "@/lib/types";
import { StatCard } from "@/components/StatCard";
import { melColors } from "@/theme/tokens";
import { FlagDetail } from "./detail";

const { Text } = Typography;
const { useBreakpoint } = Grid;

type StatusFilter = "all" | "on" | "off";

/** Cor do anel de rollout: aqua quando 100%, turquesa parcial, cinza em 0%. */
function rolloutColor(pct: number): string {
  if (pct >= 100) return melColors.aqua500;
  if (pct <= 0) return "rgba(58,90,96,0.35)";
  return melColors.turquoise500;
}

/**
 * /admin/feature-flags — flags de funcionalidade (GET/PATCH /admin/feature-flags).
 *
 * Como obtém os dados (MOCK MODE): `useTable` lista as flags paginadas/filtradas
 * via dataProvider (FIXTURES["feature-flags"]); um `useList` com
 * `pagination: { mode: "off" }` traz todas para os cards de resumo. O toggle e o
 * rollout escrevem com `useUpdate` (PATCH /admin/feature-flags/:id) em modo
 * otimista — o mock faz merge em memória e bumpa `updated_at`. Com a API real
 * (VITE_MOCK=false) nada muda na tela.
 */
export const FeatureFlagList: React.FC = () => {
  const screens = useBreakpoint();
  const [status, setStatus] = useState<StatusFilter>("all");

  const { tableProps, setFilters } = useTable<FeatureFlag>({
    syncWithLocation: true,
    sorters: { initial: [{ field: "key", order: "asc" }] },
    pagination: { pageSize: 10 },
  });

  // Todas as flags (sem paginação) para os cards de resumo.
  const { data: allData, isLoading: loadingAll } = useList<FeatureFlag>({
    resource: "feature-flags",
    pagination: { mode: "off" },
  });
  const all = useMemo(() => allData?.data ?? [], [allData]);

  const summary = useMemo(() => {
    const total = all.length;
    const enabled = all.filter((f) => f.enabled).length;
    const partial = all.filter(
      (f) => f.enabled && f.rollout_percent > 0 && f.rollout_percent < 100,
    ).length;
    const fullyOut = all.filter((f) => f.enabled && f.rollout_percent >= 100).length;
    const lastUpdated = all
      .map((f) => f.updated_at)
      .filter(Boolean)
      .sort()
      .at(-1);
    return { total, enabled, partial, fullyOut, lastUpdated };
  }, [all]);

  const { mutate } = useUpdate();

  const toggle = (row: FeatureFlag, enabled: boolean) => {
    mutate({
      resource: "feature-flags",
      id: row.id,
      values: { enabled },
      mutationMode: "optimistic",
      successNotification: () => ({
        type: "success",
        message: enabled ? "Flag ativada" : "Flag desativada",
        description: row.key,
      }),
    });
  };

  const onStatusChange = (next: StatusFilter) => {
    setStatus(next);
    setFilters(
      next === "all"
        ? []
        : [{ field: "enabled", operator: "eq", value: next === "on" }],
      "replace",
    );
  };

  return (
    <List
      title="Feature flags"
      headerProps={{
        subTitle: "Liga/desliga e rollout gradual de funcionalidades",
      }}
    >
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>
          <StatCard
            title="Flags no total"
            value={summary.total}
            prefix={<FlagOutlined style={{ color: melColors.aqua600 }} />}
            loading={loadingAll}
            hint="configuradas no sistema"
          />
        </Col>
        <Col xs={12} md={6}>
          <StatCard
            title="Ativas"
            value={summary.enabled}
            prefix={<CheckCircleTwoTone twoToneColor={melColors.success} />}
            loading={loadingAll}
            hint={`${summary.total - summary.enabled} desligadas`}
          />
        </Col>
        <Col xs={12} md={6}>
          <StatCard
            title="Em rollout parcial"
            value={summary.partial}
            prefix={<ThunderboltOutlined style={{ color: melColors.warning }} />}
            loading={loadingAll}
            hint="entre 1% e 99%"
          />
        </Col>
        <Col xs={12} md={6}>
          <StatCard
            title="100% liberadas"
            value={summary.fullyOut}
            prefix={<CheckCircleTwoTone twoToneColor={melColors.aqua500} />}
            loading={loadingAll}
            hint="disponíveis para todos"
          />
        </Col>
      </Row>

      <Card className="mel-glass" bordered={false} style={{ marginBottom: 16 }}>
        <Space
          style={{ width: "100%", justifyContent: "space-between" }}
          wrap
        >
          <Radio.Group
            value={status}
            onChange={(e) => onStatusChange(e.target.value as StatusFilter)}
            optionType="button"
            buttonStyle="solid"
          >
            <Radio.Button value="all">Todas</Radio.Button>
            <Radio.Button value="on">Ativas</Radio.Button>
            <Radio.Button value="off">Desligadas</Radio.Button>
          </Radio.Group>
          {summary.lastUpdated && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Última alteração:{" "}
              <DateField value={summary.lastUpdated} format="DD/MM/YYYY HH:mm" />
            </Text>
          )}
        </Space>
      </Card>

      <Card className="mel-glass" bordered={false} title="Flags">
        <Table
          {...tableProps}
          rowKey="id"
          scroll={{ x: 880 }}
          locale={{ emptyText: "Nenhuma flag para o filtro atual." }}
          expandable={{
            expandedRowRender: (row) => <FlagDetail flag={row} />,
            rowExpandable: () => true,
          }}
        >
          <Table.Column<FeatureFlag>
            title="Flag"
            dataIndex="key"
            sorter
            render={(_, row) => (
              <Space direction="vertical" size={2}>
                <Space size={6}>
                  {row.enabled ? (
                    <CheckCircleTwoTone twoToneColor={melColors.success} />
                  ) : (
                    <StopTwoTone twoToneColor={melColors.inkSoft} />
                  )}
                  <Text strong>
                    <code>{row.key}</code>
                  </Text>
                </Space>
                <Text type="secondary" style={{ fontSize: 12, maxWidth: 360 }}>
                  {row.description ?? "Sem descrição"}
                </Text>
              </Space>
            )}
          />
          <Table.Column<FeatureFlag>
            title="Ativa"
            dataIndex="enabled"
            width={90}
            sorter
            render={(_, row) => (
              <Switch
                checked={row.enabled}
                checkedChildren="on"
                unCheckedChildren="off"
                onChange={(v) => toggle(row, v)}
              />
            )}
          />
          <Table.Column<FeatureFlag>
            title="Rollout"
            dataIndex="rollout_percent"
            width={200}
            sorter
            render={(v: number, row) => {
              const pct = v ?? 0;
              return (
                <Tooltip
                  title={
                    row.enabled
                      ? `${pct}% dos usuários elegíveis`
                      : "Flag desligada — rollout não se aplica"
                  }
                >
                  <Progress
                    percent={pct}
                    size="small"
                    strokeColor={rolloutColor(pct)}
                    trailColor="rgba(45,212,191,0.12)"
                    status={row.enabled ? "active" : "normal"}
                    style={{ opacity: row.enabled ? 1 : 0.45, maxWidth: 160 }}
                  />
                </Tooltip>
              );
            }}
          />
          {screens.md && (
            <Table.Column<FeatureFlag>
              title="Atualizada"
              dataIndex="updated_at"
              sorter
              render={(v: string) =>
                v ? (
                  <DateField value={v} format="DD/MM/YYYY HH:mm" />
                ) : (
                  <Tag>—</Tag>
                )
              }
            />
          )}
          <Table.Column<FeatureFlag>
            title="Ações"
            fixed="right"
            width={80}
            render={(_, row) => (
              <EditButton hideText size="small" recordItemId={row.id} />
            )}
          />
        </Table>
      </Card>
    </List>
  );
};
